/**
 * Sending a turn and stopping one. Split out of `useAgentChat.ts` with no
 * behavior change; what each stream frame does lives in `agentChatTurnEvents.ts`.
 */
import { useCallback } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { ConnectionSummary } from '@bower/shared';

import type { ChatContext } from '../../contexts/ChatContextProvider';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { trackTurnSent } from './agentChatAnalytics';
import { chooseChatAttachmentPath, composerChips, postChatTurn } from './chatAttachmentUpload';
import {
  applyCancelMarker,
  reduceChatMessage,
  REDUCER_ONLY_EVENT_TYPES,
  toContextItems,
  type ChatMessage,
} from './agentChatReducer';
import { stopAgentTurn, type SSEEvent } from '../../services/agentChat.service';
import {
  connectorPicks,
  failRejectedTurn,
  historyInvalidator,
  markOwnTurnEnded,
  onTurnDone,
  onTurnError,
  onTurnSpreadsheetUndo,
  type ChatTurn,
  type ConnectorPickInputs,
} from './agentChatTurnEvents';
import { generateId, type ChatCore } from './useAgentChatCore';
import type { ChatTurnIds } from './useAgentChat.types';

export interface ChatTurnArgs {
  readonly chat: ChatCore;
  readonly enabled: boolean;
  readonly workspaceId: string | null;
  readonly sessionId: string;
  readonly isStreaming: boolean;
  readonly queryClient: QueryClient;
  readonly canWrite: boolean;
  readonly connectorsEnabled: boolean;
  readonly connections: readonly ConnectionSummary[] | undefined;
  readonly enabledFromActive: ConnectorPickInputs['enabledFromActive'];
  readonly webSearchEnabled: boolean;
}

/** `sendMessage` as the hook holds it: the HITL replies pass `options`. */
export type SendChatTurn = (
  text: string,
  contexts?: readonly ChatContext[],
  files?: File[],
  options?: {
    readonly toolConfirmation?: { callId: string; confirmed: boolean };
    readonly toolChoice?: { callId: string; value: string };
  },
) => Promise<ChatTurnIds | null>;

export interface ChatTurnControls {
  readonly sendMessage: SendChatTurn;
  readonly cancelMessage: () => void;
}

export function useAgentChatTurn({
  chat,
  enabled,
  workspaceId,
  sessionId,
  isStreaming,
  queryClient,
  canWrite,
  connectorsEnabled,
  connections,
  enabledFromActive,
  webSearchEnabled,
}: ChatTurnArgs): ChatTurnControls {
  const sendMessage = useCallback<SendChatTurn>(
    async (text, contexts, files, options) => {
      // A confirmation reply carries no text of its own — it resumes a paused
      // call. The server ignores the placeholder message entirely (see
      // agent_runner._resume_confirmed_tool), so the model never sees it.
      const confirmation = options?.toolConfirmation;
      const choice = options?.toolChoice;
      // Either kind of HITL reply stands in for the message, so the empty-text
      // guard has to accept both. Missing `choice` here is what would make a
      // pick a silent no-op.
      const hitlReply = confirmation ?? choice;
      if (!enabled || !workspaceId || isStreaming) return null;
      if (!hitlReply && !text.trim()) return null;

      // Reports the LEAD entity's type plus the count. Note this is a
      // property BOUNDARY, not continuity: before this change an @-pick
      // replaced the page context, so context_type was the explicit pick;
      // now the page's own entity always leads. Bound queries that span the
      // deploy with `context_count IS SET`.
      const leadContext = contexts?.[0];
      const contextType = leadContext?.type === 'project' ? 'collection' : leadContext?.type;
      const contextCount = contexts?.length ?? 0;

      // Add user message — attachments render as chips, not appended text.
      // A confirmation is a button press, not something the researcher typed,
      // so it gets no bubble; the answered dialog above is the record of it.
      const userMessage: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: text.trim(),
        timestamp: Date.now(),
        attachments: composerChips(files),
      };
      if (!hitlReply) chat.setMessages((prev) => [...prev, userMessage]);
      // A new send supersedes a refused message still waiting to be handed back.
      chat.setRefusedDraft(null);
      chat.setIsStreaming(true);
      chat.setCurrentAgent(null);

      safeAnalytics.setPersonProperty('last_bird_chat_at', new Date().toISOString());
      trackTurnSent({
        sessionId,
        confirmation,
        choice,
        contextType,
        contextCount,
        fileCount: files?.length ?? 0,
      });

      const sendStartTime = Date.now();

      // Prepare assistant message placeholder
      const assistantId = generateId();
      chat.currentAssistantIdRef.current = assistantId;
      // A HITL reply appends no user row — the answered dialog is the record of
      // it — so its only unacknowledged row is the assistant placeholder.
      // Reading a turn's rows off a message that was never added loses the whole
      // resumed turn and strands `isStreaming` on.
      const turnRowIds = hitlReply ? [assistantId] : [userMessage.id, assistantId];
      for (const id of turnRowIds) chat.unacknowledgedRowIdsRef.current.add(id);
      chat.currentTurnRowIdsRef.current = turnRowIds;
      chat.setMessages((prev) => [
        ...prev,
        {
          id: assistantId,
          role: 'assistant',
          content: '',
          timestamp: Date.now(),
          deliveryStatus: 'streaming',
        },
      ]);

      const turn: ChatTurn = {
        chat,
        sessionId,
        assistantId,
        turnRowIds,
        sendStartTime,
        contextType,
        invalidateHistory: historyInvalidator(queryClient, workspaceId, sessionId),
        // A HITL reply is a button press with a placeholder text: nothing to
        // hand back to the composer if it is refused.
        refusable: hitlReply ? null : { userMessageId: userMessage.id, text: text.trim(), files },
      };

      const onEvent = (event: SSEEvent) => {
        // Derived from the reducer, never restated: a hand-written list here is
        // what dropped every `confirmation` frame in #1619. See
        // REDUCER_ONLY_EVENT_TYPES for why the side-effecting types are excluded.
        if (REDUCER_ONLY_EVENT_TYPES.has(event.type)) {
          chat.setMessages((prev) =>
            prev.map((msg) => (msg.id === assistantId ? reduceChatMessage(msg, event) : msg)),
          );
          return;
        }
        // These settle shared turn state. A turn left before it settled (its
        // POST still waiting, or erroring) must not settle the turn now live.
        if (anotherTurnIsLive(chat, assistantId)) return;

        switch (event.type) {
          case 'agent_transfer':
            chat.setCurrentAgent(event.data.agent || null);
            break;
          case 'spreadsheet_undo':
            onTurnSpreadsheetUndo(turn, event);
            break;
          case 'done':
            onTurnDone(turn, event);
            break;
          case 'error':
            onTurnError(turn, event);
            break;
        }
      };

      try {
        const contextItems = toContextItems(contexts);
        const picks = connectorPicks({
          connections,
          connectorsEnabled,
          workspaceId,
          enabledFromActive,
          webSearchEnabled,
        });
        const path = await whileUploading(
          chat,
          files,
          chooseChatAttachmentPath({
            files,
            isHitlReply: Boolean(hitlReply),
            canWrite,
            workspaceId,
            sessionId,
            abortRef: chat.abortRef,
          }),
        );
        if (turn.refusable && path.kind === 'ids') {
          // Uploaded ahead of the POST, so already notes in the workspace: a
          // refusal must not hand those back for a second import. The ones
          // that failed were never stored, so they go back with the text.
          const saved = path.uploaded.refs.length;
          turn.refusable.files = path.failedFiles.length > 0 ? path.failedFiles : undefined;
          turn.refusable.savedFiles = saved > 0 ? { saved } : undefined;
        }
        const posted = postChatTurn(path, {
          text: text.trim(),
          sessionId,
          workspaceId,
          onEvent,
          context: contextItems,
          files,
          toolPicks: {
            enabledConnectorToolkits: picks.enabledConnectorToolkits,
            webSearchEnabled: picks.webSearchEnabled,
            toolConfirmation: confirmation,
            toolChoice: choice,
          },
        });
        // A cancel, or leaving the session or workspace, while the uploads ran
        // drops the turn: nothing is POSTed to either session, and whatever
        // aborted it already settled the rows and streaming state.
        if (posted) {
          // This turn makes the server create the row (``_ensure_session``),
          // so the id stops being client-minted: leaving it in the set would
          // keep its history query disabled forever, and returning to this
          // thread later would render whatever was last on screen.
          chat.clientMintedRef.current.delete(sessionId);
          adoptPostedTurn(chat, assistantId, await posted);
        }
      } catch {
        failIfStillCurrent(turn);
      }
      return { userMessageId: userMessage.id, assistantMessageId: assistantId };
    },
    [
      chat,
      workspaceId,
      sessionId,
      queryClient,
      isStreaming,
      connections,
      enabledFromActive,
      webSearchEnabled,
      connectorsEnabled,
      enabled,
      canWrite,
    ],
  );

  const cancelMessage = useCallback(() => {
    safeAnalytics.track(ANALYTICS_EVENTS.CHAT_CANCELLED, {
      partial_response: true,
    });
    // Capture the in-flight assistant-message id BEFORE clearing the ref —
    // otherwise the cancelled flag never gets applied and the reducer's
    // late-event guard never trips. (Caught in PR-148 review.)
    const inFlightId = chat.currentAssistantIdRef.current;
    chat.currentAssistantIdRef.current = null;
    // The service already has this turn — stopping the stream does not unsend
    // it — so its rows are the server's to supply, exactly as on `done`. Only
    // THIS turn's, though: the set can also hold a rejected send's rows, which
    // no snapshot will ever carry.
    //
    // A cancel while composer files upload (the id path) is one case where
    // the service does NOT have the turn: nothing is POSTed. The other is a
    // Stop that lands before the agent stored the message (during the quota
    // gate or upload staging, the first second or so), since the Stop now ends
    // the run on the server. Releasing the rows anyway is deliberate parity:
    // the turn is dropped (D1), so the next snapshot omitting it is the truth,
    // not a lost message.
    for (const id of chat.currentTurnRowIdsRef.current) {
      chat.unacknowledgedRowIdsRef.current.delete(id);
    }
    chat.currentTurnRowIdsRef.current = [];
    chat.abortRef.current?.abort();
    // The abort only stops this tab reading: deployed, it never reaches the
    // server. The explicit Stop is what ends the turn there, and the server
    // keeps the session until the stopped run has unwound: a resend in that
    // gap is refused, and that refusal is about this turn.
    void stopAgentTurn(sessionId).then((delivered) => {
      if (!delivered) console.warn('Chat Stop did not reach the server; the turn runs on');
    });
    chat.stoppedOwnTurnRef.current = { sessionId, at: Date.now() };
    if (inFlightId) chat.stoppedReplyRef.current = { sessionId, id: inFlightId };
    if (workspaceId && inFlightId) {
      markOwnTurnEnded(queryClient, workspaceId, sessionId);
    } else if (workspaceId) {
      // A turn this tab is not streaming (come back to mid-reply, another tab):
      // only a real read can say whether the server has let it go. Writing "not
      // running" into the cache would read as that answer, drop the stop just
      // recorded and end the re-reads with the transcript frozen.
      void queryClient.invalidateQueries({
        queryKey: ['session-history', workspaceId, sessionId],
        exact: true,
      });
    }
    chat.setIsStreaming(false);
    chat.setCurrentAgent(null);
    // Mark the in-flight assistant message as cancelled so the reducer
    // ignores any buffered SSE events that slip through after abort.
    // Also append a visible "_[cancelled]_" suffix so the chat doesn't
    // sit with an empty/half-finished assistant bubble.
    if (inFlightId) {
      chat.setMessages((prev) => applyCancelMarker(prev, inFlightId));
    }
  }, [chat, queryClient, workspaceId, sessionId]);

  return { sendMessage, cancelMessage };
}

/**
 * The turn is not the server's until it is POSTed: leaving while composer files
 * upload drops it, so `isUploadingTurn` lets the chat keep History and New shut.
 */
async function whileUploading<T>(
  chat: ChatCore,
  files: readonly File[] | undefined,
  upload: Promise<T>,
): Promise<T> {
  if (!files?.length) return upload;
  chat.setIsUploadingTurn(true);
  try {
    return await upload;
  } finally {
    chat.setIsUploadingTurn(false);
  }
}

/**
 * On the bytes path the controller only exists once headers arrive. A turn left
 * (or stopped) before then is no longer current: adopting it would let its
 * `done` clear the next conversation's turn. The server already has it, so
 * stopping the stream unsends nothing.
 */
function adoptPostedTurn(chat: ChatCore, assistantId: string, controller: AbortController) {
  if (chat.currentAssistantIdRef.current === assistantId) {
    chat.abortRef.current = controller;
  } else {
    controller.abort();
  }
}

/**
 * A different turn owns the chat now. Not "this turn is no longer current": a
 * turn's own `error` clears the ref, and the service can follow it with that
 * turn's `done` (a quota denial), which must still land.
 */
function anotherTurnIsLive(chat: ChatCore, assistantId: string): boolean {
  const live = chat.currentAssistantIdRef.current;
  return live !== null && live !== assistantId;
}

/** A rejected POST settles only its own turn, never one started since it was left. */
function failIfStillCurrent(turn: ChatTurn) {
  if (!anotherTurnIsLive(turn.chat, turn.assistantId)) failRejectedTurn(turn);
}
